import { useCallback, useEffect, useRef, useState } from 'react';
import fetchVendorsQC from '../utils/vendors/vendorsQC/fetchVendorsQC';

function useGetVendorsQC(
  landingId,
  countryId,
  latitude,
  longitude,
  landingType,
  userId,
  filters,
  headers,
  initialVendors = [],
  initialTotalVendors = 0,
  initialSize = 0,
  isMigration = false,
  initialOriginalPeyaTrace = '',
  initialMidasRequestId = '',
) {
  const loader = useRef(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(initialVendors.length === 0);
  const [vendorQcError, setVendorQcError] = useState(undefined);
  const [vendors, setVendors] = useState(initialVendors);
  const [totalVendors, setTotalVendors] = useState(initialTotalVendors);
  const [size, setSize] = useState(initialSize);
  const [hasErrorRetry, setHasErrorRetry] = useState(0);
  const [midasRequestId, setMidasRequestId] = useState(initialMidasRequestId);
  const [originalPeyaTrace, setOriginalPeyaTrace] = useState(
    initialOriginalPeyaTrace,
  );

  const getVendorsQC = async () => {
    try {
      if (totalVendors > 0 && vendors.length >= totalVendors) {
        setLoading(false);
        return;
      }

      if (isMigration && page === 1) {
        setVendors(initialVendors);
        setTotalVendors(initialTotalVendors);
        setVendorQcError(initialTotalVendors === 0 ? 404 : undefined);
        setMidasRequestId(initialMidasRequestId);
        setOriginalPeyaTrace(initialOriginalPeyaTrace);
        setSize(initialSize);
        setLoading(false);
        return;
      }

      setLoading(true);
      const { data, error } = await fetchVendorsQC(
        page,
        landingId,
        countryId,
        latitude,
        longitude,
        landingType,
        userId,
        filters,
        headers,
      );
      if (error) {
        if (error?.response?.status) {
          setVendorQcError(error.response.status);
        } else if (error?.code === 'ECONNABORTED') {
          setVendorQcError('ECONNABORTED'); // error by timeout
        } else if (typeof error === 'number') {
          setVendorQcError(error);
        } else {
          setVendorQcError(500);
        }
      } else if (data && data.vendors?.length > 0) {
        setVendors([...vendors, ...data.vendors]);
        setTotalVendors(data?.pagination?.total);
        setSize(data?.pagination.size);
        setMidasRequestId(data?.metadata?.midas_request_id);
        setOriginalPeyaTrace(data?.metadata?.original_peya_trace_id);
        setVendorQcError(undefined);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleObserver = useCallback(
    (entries) => {
      const target = entries[0];
      const hasScrolled = typeof window !== 'undefined' && window.scrollY > 0;

      if (hasErrorRetry === 3) {
        setVendorQcError(500);
      }
      if (
        target.isIntersecting &&
        vendorQcError === 'ECONNABORTED' &&
        hasErrorRetry < 3
      ) {
        setHasErrorRetry(hasErrorRetry + 1);
        setLoading(true);
      }
      if (
        target.isIntersecting &&
        !loading &&
        !vendorQcError &&
        (!isMigration || page > 1 || hasScrolled)
      ) {
        setPage((prevPage) => prevPage + 1);
        setLoading(true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vendorQcError, loading, isMigration, page, hasErrorRetry],
  );

  const reloadLanding = () => {
    setVendorQcError(undefined);
    setLoading(!loading);
  };

  const redirectToHome = () => {
    document.location = `pedidosya://home`;
  };

  useEffect(() => {
    const options = {
      root: null,
      threshold: 0,
    };

    const observer = new IntersectionObserver(handleObserver, options);
    if (loader.current) observer.observe(loader.current);
    return () => {
      observer.disconnect();
    };
  }, [handleObserver]);

  useEffect(() => {
    if (loading) {
      getVendorsQC();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    if (vendors?.length > 0) {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendors]);

  return {
    loading,
    vendorQcError,
    vendors,
    reloadLanding,
    redirectToHome,
    loader,
    totalVendors,
    size,
    page,
    midasRequestId,
    originalPeyaTrace,
  };
}

export default useGetVendorsQC;
